import { useState, useEffect } from 'react';
import { Accessibility, X, ZoomIn, ZoomOut, Moon, Sun, Link2, Type, MousePointer2, Pause, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  highlightLinks: boolean;
  readableFont: boolean;
  bigCursor: boolean;
  stopAnimations: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 100,
  highContrast: false,
  highlightLinks: false,
  readableFont: false,
  bigCursor: false,
  stopAnimations: false,
};

export function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    const saved = localStorage.getItem('accessibility-settings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  useEffect(() => {
    applySettings(settings);
  }, []);

  const applySettings = (s: AccessibilitySettings) => {
    const html = document.documentElement;
    
    // Font size
    html.style.fontSize = `${s.fontSize}%`;
    
    // High contrast
    html.classList.toggle('high-contrast', s.highContrast);
    
    // Highlight links
    html.classList.toggle('highlight-links', s.highlightLinks);
    
    // Readable font
    html.classList.toggle('readable-font', s.readableFont);
    
    // Big cursor
    html.classList.toggle('big-cursor', s.bigCursor);
    
    // Stop animations
    html.classList.toggle('stop-animations', s.stopAnimations);
  };

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSetting('fontSize', settings.fontSize + 10);
    }
  };

  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSetting('fontSize', settings.fontSize - 10);
    }
  };

  return (
    <>
      {/* Accessibility Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 start-6 z-50 p-3 rounded-full bg-blue-600 text-white shadow-lg transition-all duration-300 hover:bg-blue-700 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
        aria-label="פתח תפריט נגישות"
      >
        <Accessibility className="h-6 w-6" />
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-auto animate-scale-in" dir="rtl">
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Accessibility className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-bold text-foreground">הגדרות נגישות</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                aria-label="סגור"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Font Size */}
              <div className="bg-secondary/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-foreground flex items-center gap-2">
                    <Type className="h-5 w-5" />
                    גודל טקסט
                  </span>
                  <span className="text-sm text-muted-foreground">{settings.fontSize}%</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={decreaseFontSize}
                    disabled={settings.fontSize <= 80}
                    className="flex-1 py-2 px-4 rounded-lg bg-card border border-border hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ZoomOut className="h-5 w-5 mx-auto" />
                  </button>
                  <button
                    onClick={increaseFontSize}
                    disabled={settings.fontSize >= 150}
                    className="flex-1 py-2 px-4 rounded-lg bg-card border border-border hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ZoomIn className="h-5 w-5 mx-auto" />
                  </button>
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-3">
                <ToggleOption
                  icon={settings.highContrast ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                  label="ניגודיות גבוהה"
                  checked={settings.highContrast}
                  onChange={(v) => updateSetting('highContrast', v)}
                />
                
                <ToggleOption
                  icon={<Link2 className="h-5 w-5" />}
                  label="הדגשת קישורים"
                  checked={settings.highlightLinks}
                  onChange={(v) => updateSetting('highlightLinks', v)}
                />
                
                <ToggleOption
                  icon={<Type className="h-5 w-5" />}
                  label="גופן קריא"
                  checked={settings.readableFont}
                  onChange={(v) => updateSetting('readableFont', v)}
                />
                
                <ToggleOption
                  icon={<MousePointer2 className="h-5 w-5" />}
                  label="סמן גדול"
                  checked={settings.bigCursor}
                  onChange={(v) => updateSetting('bigCursor', v)}
                />
                
                <ToggleOption
                  icon={<Pause className="h-5 w-5" />}
                  label="עצירת אנימציות"
                  checked={settings.stopAnimations}
                  onChange={(v) => updateSetting('stopAnimations', v)}
                />
              </div>

              {/* Reset Button */}
              <button
                onClick={resetSettings}
                className="w-full py-3 px-4 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors flex items-center justify-center gap-2 text-foreground"
              >
                <RotateCcw className="h-5 w-5" />
                איפוס הגדרות
              </button>

              {/* Accessibility Statement Link */}
              <a
                href="/accessibility"
                className="block w-full py-3 px-4 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-center font-medium"
              >
                הצהרת נגישות
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface ToggleOptionProps {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleOption({ icon, label, checked, onChange }: ToggleOptionProps) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={cn(
        'w-full flex items-center justify-between p-4 rounded-xl border transition-colors',
        checked
          ? 'bg-primary/10 border-primary text-primary'
          : 'bg-card border-border hover:bg-secondary'
      )}
    >
      <span className="flex items-center gap-3 font-medium">
        {icon}
        {label}
      </span>
      <div
        className={cn(
          'w-12 h-6 rounded-full transition-colors relative',
          checked ? 'bg-primary' : 'bg-muted'
        )}
      >
        <div
          className={cn(
            'absolute top-1 w-4 h-4 rounded-full bg-white transition-all',
            checked ? 'end-1' : 'start-1'
          )}
        />
      </div>
    </button>
  );
}
